import api from 'api/instance';
import { FetchQueryType, FetchResponse } from 'types/common';

import { Employee, RolesEnum } from 'types/user';

const URL = `${process.env.REACT_APP_API_URL}`;

type FetchEmployeesParams = FetchQueryType & {
  role?: RolesEnum;
  getAll?: string | boolean;
  status?: string | string[];
};

export const fetchEmployees = async (params: FetchEmployeesParams) => {
  const { data } = await api.get<FetchResponse<Employee>>(
    `${URL}/user/employees/list`,
    {
      params,
    },
  );

  return data;
};

type CreateEmployeePayload = {
  fname: string;
  lname: string;
  role: RolesEnum;
  phone: string;
  email: string;
  washHub: string;
  washHubCode: string;
};

export const createEmployee = async (payload: CreateEmployeePayload) => {
  const response = await api.post(`${URL}/user/employee/create`, payload);

  return response?.data;
};

export const deleteEmployees = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/user/remove-many`, {
    data: { ids },
  });

  return data;
};

export const updateEmployee = async (payload: Employee) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/user/employee/${_id}`, _payload);

  return data;
};

const getPermissionWashHubId = (washHub: unknown): string => {
  if (typeof washHub === 'string') {
    return washHub;
  }

  if (washHub && typeof washHub === 'object' && '_id' in washHub) {
    return String((washHub as { _id?: string })._id ?? '');
  }

  return '';
};

type ShipToContactUser = {
  _id: string;
  fname?: string;
  lname?: string;
  permissions?: Array<{
    role?: string;
    washHub?: unknown;
  }>;
};

const getShipToContactRoleForHub = (
  user: ShipToContactUser,
  washHubId: string,
): string | undefined =>
  user.permissions?.find(
    (permission) =>
      getPermissionWashHubId(permission.washHub) === washHubId &&
      (permission.role === RolesEnum.WASH_HUB_ADMIN ||
        permission.role === RolesEnum.SUPER_ADMIN),
  )?.role;

export const hasShipToContactPermissionForHub = (
  user: ShipToContactUser,
  washHubId: string,
) => Boolean(getShipToContactRoleForHub(user, washHubId));

export const getDefaultShipToContactId = (
  users: ShipToContactUser[],
  washHubId: string,
) => {
  const hubManager = users.find(
    (user) =>
      getShipToContactRoleForHub(user, washHubId) === RolesEnum.WASH_HUB_ADMIN,
  );

  return hubManager?._id ?? users[0]?._id ?? '';
};

export const fetchWashHubAdminsForLocation = async (washHubId: string) => {
  const [hubUsersResponse, superAdminsResponse] = await Promise.all([
    fetchEmployees({
      washHub: washHubId,
      limit: 1000,
      getAll: 'true',
    }),
    fetchEmployees({
      washHub: washHubId,
      role: RolesEnum.SUPER_ADMIN,
      limit: 1000,
      getAll: 'true',
    }),
  ]);

  const rolePriority = (role?: string) => {
    if (role === RolesEnum.WASH_HUB_ADMIN) {
      return 0;
    }

    if (role === RolesEnum.SUPER_ADMIN) {
      return 1;
    }

    return 2;
  };

  const combined = [
    ...(hubUsersResponse?.items ?? []),
    ...(superAdminsResponse?.items ?? []),
  ];

  const uniqueAdmins = Array.from(
    new Map(combined.map((admin) => [admin._id, admin])).values(),
  ).filter((admin) => hasShipToContactPermissionForHub(admin, washHubId));

  return uniqueAdmins.sort((a, b) => {
    const roleDiff =
      rolePriority(getShipToContactRoleForHub(a, washHubId)) -
      rolePriority(getShipToContactRoleForHub(b, washHubId));

    if (roleDiff !== 0) {
      return roleDiff;
    }

    return `${a.fname ?? ''} ${a.lname ?? ''}`.localeCompare(
      `${b.fname ?? ''} ${b.lname ?? ''}`,
    );
  });
};
